export interface RunnerSignatureFields {
  method: string;
  path: string;
  timestamp: number;
  nonce: string;
  body: string;
}

const encoder = new TextEncoder();

function bytesToHex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function bodyDigest(body: string): Promise<string> {
  return bytesToHex(await crypto.subtle.digest("SHA-256", encoder.encode(body)));
}

async function signaturePayload(fields: RunnerSignatureFields): Promise<string> {
  return [fields.method.toUpperCase(), fields.path, String(fields.timestamp), fields.nonce, await bodyDigest(fields.body)].join("\n");
}

export async function createRunnerSignature(secret: string, fields: RunnerSignatureFields): Promise<string> {
  const key = await crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return bytesToHex(await crypto.subtle.sign("HMAC", key, encoder.encode(await signaturePayload(fields))));
}

export async function verifyRunnerSignature(secret: string, fields: RunnerSignatureFields, signature: string): Promise<boolean> {
  if (!/^[a-f0-9]{64}$/.test(signature)) return false;
  const key = await crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["verify"]);
  const signatureBytes = Uint8Array.from(signature.match(/.{2}/g) ?? [], (byte) => Number.parseInt(byte, 16));
  return crypto.subtle.verify("HMAC", key, signatureBytes, encoder.encode(await signaturePayload(fields)));
}
